import { useEffect, useMemo, useState } from 'react';
import { accountsApi, type AdPlatform, type AdFlatNode, type AdSetFlatNode, type CampaignFlatNode } from '../api/accounts';
import useAppStore from '../store/useAppStore';
import { MultiSelect, type SelectOption } from './MultiSelect';

export type AdEntityFilterField = 'campaign_id' | 'campaign_name' | 'adset_name' | 'ad_name';
export type AdTextFilterOperator = 'contains' | 'not_contains';

export type AdTextFilterRule = {
  field: 'campaign_name' | 'adset_name' | 'ad_name';
  operator: AdTextFilterOperator;
  value: string;
};

type SearchOption = SelectOption & { searchText: string };

const searchEntityFor = (field: AdEntityFilterField) => {
  if (field === 'campaign_id' || field === 'campaign_name') return 'campaign';
  if (field === 'adset_name') return 'adset';
  return 'ad';
};

const optionForCampaign = (item: CampaignFlatNode, field: AdEntityFilterField): SearchOption => ({
  value: field === 'campaign_id' ? item.third_ad_campaign_id : item.name,
  label: field === 'campaign_id' ? `${item.third_ad_campaign_id} · ${item.name}` : item.name,
  searchText: `${item.third_ad_campaign_id} ${item.name}`,
});

const optionForAdset = (item: AdSetFlatNode): SearchOption => ({
  value: item.name,
  label: item.name,
  searchText: `${item.third_ad_set_id} ${item.name}`,
});

const optionForAd = (item: AdFlatNode): SearchOption => ({
  value: item.name,
  label: item.name,
  searchText: `${item.third_ad_id} ${item.name}`,
});

export function AdEntitySearchSelect({
  label,
  field,
  platform,
  value,
  onChange,
  width = 220,
}: {
  label: string;
  field: AdEntityFilterField;
  platform: AdPlatform;
  value: string;
  onChange: (value: string) => void;
  width?: number;
}) {
  const { brandId, siteId } = useAppStore();
  const [search, setSearch] = useState('');
  const [options, setOptions] = useState<SearchOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const entity = searchEntityFor(field);

  useEffect(() => {
    const keyword = search.trim();
    if (keyword.length < 2 || !brandId) {
      setLoading(false);
      setError('');
      return;
    }

    let active = true;
    const timer = window.setTimeout(async () => {
      setLoading(true);
      setError('');
      try {
        const params = {
          brand_id: brandId,
          ...(siteId ? { site_id: siteId } : {}),
          platform,
          search: keyword,
          page: 1,
          size: 20,
        } as const;
        const response = entity === 'campaign'
          ? await accountsApi.listFacebookCampaigns(params)
          : entity === 'adset'
            ? await accountsApi.listFacebookAdSets(params)
            : await accountsApi.listFacebookAds(params);
        if (!active) return;

        const list = response.list ?? [];
        const next = entity === 'campaign'
          ? (list as CampaignFlatNode[]).map(item => optionForCampaign(item, field))
          : entity === 'adset'
            ? (list as AdSetFlatNode[]).map(optionForAdset)
            : (list as AdFlatNode[]).map(optionForAd);
        setOptions(next);
      } catch {
        if (active) {
          setOptions([]);
          setError('广告数据加载失败');
        }
      } finally {
        if (active) setLoading(false);
      }
    }, 250);
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [brandId, entity, field, platform, search, siteId]);

  const visibleOptions = useMemo(() => {
    if (!value || options.some(option => option.value === value)) return options;
    return [{ value, label: value, searchText: value }, ...options];
  }, [options, value]);

  const emptyText = search.trim().length < 2
    ? '请输入至少 2 个字符搜索'
    : '未找到匹配的广告数据';

  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: width }}>
      <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--text3)' }}>{label}</span>
      <MultiSelect
        single
        clearable
        searchable
        options={visibleOptions}
        value={value ? [value] : []}
        onChange={next => onChange(next[0] ?? '')}
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="至少输入 2 个字符"
        loading={loading}
        errorText={error}
        emptyText={emptyText}
        style={{ width }}
      />
    </label>
  );
}

export function AdTextFilterModal({
  title,
  field,
  value,
  onConfirm,
}: {
  title: string;
  field: AdTextFilterRule['field'];
  value: AdTextFilterRule[];
  onConfirm: (value: AdTextFilterRule[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<AdTextFilterRule[]>(value);

  const openModal = () => {
    setDraft(value.map(rule => ({ ...rule, field })));
    setOpen(true);
  };

  const updateRule = (index: number, patch: Partial<AdTextFilterRule>) => {
    setDraft(current => current.map((rule, ruleIndex) => ruleIndex === index ? { ...rule, ...patch, field } : rule));
  };

  return (
    <>
      <button type="button" className="btn btn-outline btn-sm" onClick={openModal} style={{ height: 34, padding: '0 12px' }}>
        {title}{value.length > 0 ? `（${value.length}）` : ''}
      </button>
      {open && (
        <div role="dialog" aria-modal="true" aria-label={title} style={{ position: 'fixed', inset: 0, zIndex: 90, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, background: 'rgba(15, 23, 42, .36)' }}>
          <div style={{ width: 'min(760px, calc(100vw - 32px))', maxHeight: 'calc(100vh - 32px)', overflow: 'auto', padding: 20, borderRadius: 'var(--radius)', background: 'var(--surface)', boxShadow: '0 16px 48px rgba(15,23,42,.24)' }}>
            <div style={{ marginBottom: 16, fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>{title}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {draft.map((rule, index) => (
                <div key={`${field}-${index}`} style={{ display: 'flex', alignItems: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
                  <MultiSelect
                    single
                    clearable={false}
                    options={[{ value: 'contains', label: '包含' }, { value: 'not_contains', label: '不包含' }]}
                    value={[rule.operator]}
                    onChange={next => updateRule(index, { operator: (next[0] ?? 'contains') as AdTextFilterOperator })}
                    style={{ width: 110 }}
                  />
                  <label style={{ display: 'flex', flexDirection: 'column', gap: 4, width: 360 }}>
                    <span style={{ minHeight: 13, fontSize: 11, fontWeight: 500, color: 'var(--text3)' }}>{index === 0 ? title.replace('组合筛选', '') : ''}</span>
                    <input
                      className="form-input"
                      value={rule.value}
                      placeholder="请输入文本"
                      onChange={event => updateRule(index, { value: event.target.value })}
                      style={{ height: 34, width: '100%' }}
                    />
                  </label>
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => setDraft(current => current.filter((_, ruleIndex) => ruleIndex !== index))}>移除</button>
                </div>
              ))}
              {draft.length === 0 && <div style={{ color: 'var(--text2)', fontSize: 13 }}>可添加包含或不包含条件；填写后点击确定才会应用筛选。</div>}
              <div>
                <button type="button" className="btn btn-outline btn-sm" onClick={() => setDraft(current => [...current, { field, operator: 'contains', value: '' }])}>添加条件</button>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
              <button type="button" className="btn btn-ghost" onClick={() => setOpen(false)}>取消</button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  onConfirm(draft.filter(rule => rule.value.trim() !== '').map(rule => ({ ...rule, field })));
                  setOpen(false);
                }}
              >
                确定
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
